import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { formatCents, type Locale } from '@btw/shared';
import { nl, type MessageKey } from './nl';

export type { Locale, MessageKey };
export type Params = Record<string, string | number | null | undefined>;
export type TFunction = (key: MessageKey, params?: Params) => string;

/**
 * Dictionaries (review 2026-10-28, bundle size): Dutch — also the fallback — ships in the main bundle; English and
 * Chinese are separate chunks loaded when needed (main.tsx loads the starting language before the first render).
 */
const DICTS: Partial<Record<Locale, Record<MessageKey, string>>> = { nl };
const LOADERS: Record<Exclude<Locale, 'nl'>, () => Promise<Record<MessageKey, string>>> = {
  en: () => import('./en').then((m) => m.en),
  zh: () => import('./zh').then((m) => m.zh),
};
const loading: Partial<Record<Locale, Promise<void>>> = {};

export function isLocaleLoaded(locale: Locale): boolean {
  return !!DICTS[locale];
}

export function loadLocale(locale: Locale): Promise<void> {
  if (DICTS[locale]) return Promise.resolve();
  return (loading[locale] ??= LOADERS[locale as Exclude<Locale, 'nl'>]().then(
    (dict) => {
      DICTS[locale] = dict;
    },
    (e) => {
      delete loading[locale];
      throw e;
    },
  ));
}

/** Server rendering and tests: make a dictionary available synchronously. */
export function registerLocale(locale: Locale, dict: Record<MessageKey, string>): void {
  DICTS[locale] = dict;
}
export const LOCALES: Locale[] = ['nl', 'en', 'zh'];
const INTL_TAG: Record<Locale, string> = { nl: 'nl-NL', en: 'en-GB', zh: 'zh-CN' };

/**
 * Minimal ICU-style interpolation: `{name}` and `{n, plural, one {# vraag} other {# vragen}}`.
 * Kept tiny on purpose: the dictionaries only need numeric plurals.
 */
export function interpolate(template: string, params: Params | undefined, locale: Locale): string {
  if (!params) return template;
  let out = '';
  let i = 0;
  while (i < template.length) {
    const open = template.indexOf('{', i);
    if (open === -1) {
      out += template.slice(i);
      break;
    }
    out += template.slice(i, open);
    const close = matchingBrace(template, open);
    const inner = template.slice(open + 1, close);
    out += renderPlaceholder(inner, params, locale);
    i = close + 1;
  }
  return out;
}

function matchingBrace(s: string, open: number): number {
  let depth = 0;
  for (let j = open; j < s.length; j++) {
    if (s[j] === '{') depth++;
    else if (s[j] === '}' && --depth === 0) return j;
  }
  return s.length - 1;
}

const pluralRules: Partial<Record<Locale, Intl.PluralRules>> = {};
function renderPlaceholder(inner: string, params: Params, locale: Locale): string {
  const pluralMatch = /^\s*(\w+)\s*,\s*plural\s*,(.*)$/s.exec(inner);
  if (!pluralMatch) {
    const v = params[inner.trim()];
    return v === null || v === undefined ? '' : String(v);
  }
  const name = pluralMatch[1];
  const n = Number(params[name] ?? 0);
  const branches: Record<string, string> = {};
  const body = pluralMatch[2];
  const re = /(=\d+|zero|one|two|few|many|other)\s*\{/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body))) {
    const start = m.index + m[0].length - 1;
    const end = matchingBrace(body, start);
    branches[m[1]] = body.slice(start + 1, end);
    re.lastIndex = end + 1;
  }
  const rules = (pluralRules[locale] ??= new Intl.PluralRules(INTL_TAG[locale]));
  const branch = branches[`=${n}`] ?? branches[rules.select(n)] ?? branches.other ?? '';
  return interpolate(branch.replace(/#/g, String(n)), params, locale);
}

export function translate(locale: Locale, key: MessageKey, params?: Params): string {
  const template = DICTS[locale]?.[key] ?? nl[key] ?? key;
  return interpolate(template, params, locale);
}

/**
 * <html lang>: the language the page is written in. App screens follow the UI language; a website page (NL or EN,
 * whatever the UI language) declares its own through setPageLanguage — in one place, so the order in which React runs
 * the effects cannot leave the wrong one (2026-11-03).
 */
let uiLang = 'nl';
/** Every mounted website layout holds a claim (two can be mounted for a moment while a page changes). */
const pageLangClaims: Array<{ lang: string }> = [];
function applyPageLang() {
  if (typeof document !== 'undefined') document.documentElement.lang = pageLangClaims[pageLangClaims.length - 1]?.lang ?? uiLang;
}
/** Declares the page's language while mounted; returns the release. */
export function claimPageLanguage(lang: string): () => void {
  const claim = { lang };
  pageLangClaims.push(claim);
  applyPageLang();
  return () => {
    const i = pageLangClaims.indexOf(claim);
    if (i >= 0) pageLangClaims.splice(i, 1);
    applyPageLang();
  };
}

export function hasKey(key: string): key is MessageKey {
  return key in nl;
}

/* ───────── Formatting (§8.3, §10.1) ───────── */
const UTC = 'UTC';
function parseDate(iso: string): Date {
  return iso.length === 10 ? new Date(`${iso}T00:00:00Z`) : new Date(iso);
}

export interface Formatters {
  /** Integer cents → "€ 2.480,00" (nl) / "€2,480.00" (en, zh). */
  money: (cents: number, opts?: { decimals?: boolean }) => string;
  /** Whole euros → "€ 2.480". */
  euro: (euros: number) => string;
  number: (n: number) => string;
  /** "14 okt 2026" / "14 Oct 2026" / "2026年10月14日". */
  date: (iso: string) => string;
  dateShort: (iso: string) => string;
  /** "14-10-2026 10:12" (nl) style timestamp. */
  dateTime: (iso: string) => string;
  time: (iso: string) => string;
  weekday: (iso: string) => string;
  /** "Q3 2026" in every language. */
  quarter: (key: string) => string;
  /** Relative day label for activity feeds. */
  relativeDay: (iso: string, today?: string) => string;
  /** Spoken amount for screen readers, e.g. "2480 euro". */
  spokenEuro: (euros: number) => string;
  percent: (n: number) => string;
}

function makeFormatters(locale: Locale, t: TFunction): Formatters {
  const tag = INTL_TAG[locale];
  const dateFmt =
    locale === 'zh'
      ? new Intl.DateTimeFormat(tag, { year: 'numeric', month: 'long', day: 'numeric', timeZone: UTC })
      : new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short', year: 'numeric', timeZone: UTC });
  const dateShortFmt =
    locale === 'zh'
      ? new Intl.DateTimeFormat(tag, { month: 'long', day: 'numeric', timeZone: UTC })
      : new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short', timeZone: UTC });
  const timeFmt = new Intl.DateTimeFormat(tag, { hour: '2-digit', minute: '2-digit' });
  const dtFmt = new Intl.DateTimeFormat(tag, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const numFmt = new Intl.NumberFormat(tag);
  const wdFmt = new Intl.DateTimeFormat(tag, { weekday: 'long', timeZone: UTC });
  const pctFmt = new Intl.NumberFormat(tag, { style: 'percent', maximumFractionDigits: 1 });
  const stripDot = (s: string) => (locale === 'nl' ? s.replace(/\.(?=\s|$)/g, '') : locale === 'en' ? s.replace(/\bSept\b/, 'Sep') : s);
  return {
    money: (cents, opts) => formatCents(cents, locale, opts),
    euro: (euros) => formatCents(Math.round(euros) * 100, locale, { decimals: false }),
    number: (n) => numFmt.format(n),
    date: (iso) => stripDot(dateFmt.format(parseDate(iso))),
    dateShort: (iso) => stripDot(dateShortFmt.format(parseDate(iso))),
    dateTime: (iso) => dtFmt.format(parseDate(iso)).replace(',', ''),
    time: (iso) => timeFmt.format(parseDate(iso)),
    weekday: (iso) => wdFmt.format(parseDate(iso)),
    quarter: (key) => {
      const m = /^(\d{4})-Q([1-4])$/.exec(key);
      return m ? `Q${m[2]} ${m[1]}` : key;
    },
    relativeDay: (iso, today) => {
      const d = parseDate(iso);
      const base = today ? parseDate(today) : new Date();
      const startOf = (x: Date) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate());
      const days = Math.round((startOf(base) - startOf(d)) / 86_400_000);
      if (days <= 0 && iso.length > 10) return timeFmt.format(d);
      if (days === 1) return t('time.yesterday');
      return stripDot(dateShortFmt.format(d));
    },
    spokenEuro: (euros) => t('a11y.euroSpoken', { n: Math.abs(Math.round(euros)) }),
    percent: (n) => pctFmt.format(n),
  };
}

interface I18nValue {
  locale: Locale;
  t: TFunction;
  fmt: Formatters;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ locale: wanted, children }: { locale: Locale; children: ReactNode }) {
  // Until a newly chosen language has loaded, keep showing the previous one (no flash of Dutch in between).
  const [shown, setShown] = useState<Locale>(() => (isLocaleLoaded(wanted) ? wanted : 'nl'));
  const locale = isLocaleLoaded(wanted) ? wanted : shown;
  useEffect(() => {
    let alive = true;
    if (isLocaleLoaded(wanted)) setShown(wanted);
    else
      loadLocale(wanted).then(
        () => alive && setShown(wanted),
        () => undefined,
      );
    return () => {
      alive = false;
    };
  }, [wanted]);
  const t = useCallback<TFunction>((key, params) => translate(locale, key, params), [locale]);
  const value = useMemo<I18nValue>(() => ({ locale, t, fmt: makeFormatters(locale, t) }), [locale, t]);
  // The page language follows the UI language, so browsers do not offer to "translate from Dutch" a Chinese or English
  // screen (Chrome's translation turned the EN switch into "AND" and mixed English into Chinese pages).
  useEffect(() => {
    uiLang = locale === 'zh' ? 'zh-CN' : locale;
    applyPageLang();
    // IBM Plex Sans SC only for the Chinese UI: a separate stylesheet whose subsets load per character range.
    if (locale === 'zh') void import('../styles/plex-sans-sc.css').catch(() => undefined);
  }, [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const v = useContext(I18nContext);
  if (!v) throw new Error('useI18n outside I18nProvider');
  return v;
}

export function useT(): TFunction {
  return useI18n().t;
}

/** Best guess before the user is known: stored preference, else browser language (§10.1). */
export function guessLocale(): Locale {
  try {
    const stored = localStorage.getItem('btw.locale');
    if (stored === 'nl' || stored === 'en' || stored === 'zh') return stored;
  } catch {
    /* storage blocked */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language.toLowerCase() : 'nl';
  if (nav.startsWith('zh')) return 'zh';
  if (nav.startsWith('en')) return 'en';
  return 'nl';
}

export function intlTag(locale: Locale): string {
  return INTL_TAG[locale];
}
